import { LoadingOutlined } from '@ant-design/icons';
import { useFlag } from '@unleash/proxy-client-react';
import { Popover, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  ArrowLeftSvg,
  CrossSvg,
  DeleteSvg,
  ExclamationMarkSvg,
  PlusSvg,
} from '../../../../assets/icons';
import ArrowRightSvg from '../../../../assets/icons/ArrowRightSvg';
import FoldedHandsSVG from '../../../../assets/icons/FoldedHands';
import FolderSvg from '../../../../assets/icons/FolderSvg';
import GoogleDriveSvg from '../../../../assets/icons/GoogleDriveSvg';
import LinkSvg from '../../../../assets/icons/LinkSvg';
import MoreSvg from '../../../../assets/icons/MoreSvg';
import UploadsSvg from '../../../../assets/icons/UploadsSvg';
import theme from '../../../../assets/theme';
import useAdjustAttachmentReadability from '../../../../hooks/useAdjustAttachmentReadability';
import { useAttachmentContent } from '../../../../hooks/useAttachmentContent';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useCreateAttachment from '../../../../hooks/useCreateAttachment';
import useCreateAttachmentContentFromFile from '../../../../hooks/useCreateAttachmentContentFromFile';
import {
  allPromptOutputsSelector,
  attachmentsSelector,
  presentationIdSelector,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useEditAttachment from '../../../../hooks/useEditAttachment';
import useGoogleDrive from '../../../../hooks/useGoogleDrive';
import { useMetrics } from '../../../../hooks/useMetrics';
import { useUploadGoogleDrive } from '../../../../hooks/useUploadGoogleDrive';
import { useUploadPresentation } from '../../../../hooks/useUploadPresentation';
import { useAvailablePromptDependencies } from '../../../../hooks/useVisiblePromptTestCaseInputs';
import { LessonAttachment } from '../../../../services/backendService/backendService';
import { generateUniqueId } from '../../../../utils/creator';
import { isIos } from '../../../../utils/navigator';
import Select from '../../../_atoms/_Select/Select';
import { Button } from '../../../_atoms/Button';
import { useGoogleDrivePicker } from '../../../_atoms/GoogleDrivePicker';
import { CallbackDoc } from '../../../_atoms/GoogleDrivePicker/typeDefs';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import BaseModal from '../../../_molecules/BaseModal';
import { FloatingSelect } from '../../../dynamicPages/CreatePage/components/FloatingSelect';
import { UploadedFile } from '../../../dynamicPages/CreatePage/types';
import { parseSlateStringToText } from '../../../SlideComponents/slateUtil';
import { SlideElement } from '../../../SlideComponents/slide.types';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import { PromptTextEditor, PromptTextEditorV2 } from '../PromptsTab/PromptTextEditor';

const EditAttachmentForm = ({
  onDone,
  attachment,
}: {
  onDone: () => void;
  attachment: LessonAttachment;
}) => {
  const [localAttachment, setLocalAttachment] = useState<LessonAttachment>(attachment);

  const dataDispatch = useDesignerDataContext(context => context.dispatch);

  const { content } = useAttachmentContent(attachment, false);

  const { editAttachment, isSavingAttachment } = useEditAttachment();

  const promptInputs = useDesignerDataContext(
    c => c.state.localTemplate?.aiContent?.promptInputs,
  );

  useEffect(() => {
    if (attachment.title !== localAttachment.title) {
      setLocalAttachment(attachment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment]);

  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);

  const { availablePromptDependencies } = useAvailablePromptDependencies({
    promptId: 'notused',
    promptInputs,
    promptOutputs,
  });

  const [localAttachmentContent, setLocalAttachmentContent] = useState<string>();

  useEffect(() => {
    if (content) {
      setLocalAttachmentContent(JSON.stringify(content));
    }
  }, [content]);

  const handleSaveAttachment = () => {
    if (localAttachmentContent === JSON.stringify(content)) {
      // No changes to the passage, we simply save the title
      dataDispatch({
        type: TemplateStateActions.attachmentEdited,
        attachment: {
          ...attachment,
          title: localAttachment.title,
        },
      });
      onDone();
      return;
    }

    if (localAttachment && localAttachmentContent) {
      editAttachment({
        data: {
          title: localAttachment.title,
          content: localAttachmentContent,
          attachmentId: attachment.attachmentId,
        },
        onSuccessFunction: (data: LessonAttachment) => {
          dataDispatch({
            type: TemplateStateActions.attachmentEdited,
            attachment: data,
          });
          onDone();
        },
      });
    }
  };

  return (
    <>
      <div style={{ display: 'flex', marginTop: '0px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>Title</p>
      </div>
      <PromptContainer>
        <PromptTextEditorV2
          availablePromptDependencies={availablePromptDependencies}
          value={localAttachment.title}
          maxLength={150}
          slideId={`title.${attachment.attachmentId}`}
          placeholder={'A poem about the ocean'}
          style={{ borderRadius: '5px' }}
          onChange={title => setLocalAttachment({ ...localAttachment, title })}
        />
      </PromptContainer>
      <div style={{ display: 'flex', marginTop: '20px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>Text to read</p>
      </div>
      <PromptContainer style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {localAttachmentContent && (
          <PromptTextEditorV2
            availablePromptDependencies={availablePromptDependencies}
            value={localAttachmentContent}
            maxLength={90000}
            slideId={`content.${attachment.attachmentId}`}
            placeholder={'The poem itself'}
            style={{ borderRadius: '5px', minHeight: '150px' }}
            onChange={content => setLocalAttachmentContent(content)}
          />
        )}
      </PromptContainer>
      <div style={{ marginTop: 20 }}>
        <Button
          styles={{ width: '100%' }}
          type="primary"
          onClick={() => handleSaveAttachment()}
          loading={isSavingAttachment}
        >
          Save
        </Button>
      </div>
    </>
  );
};
const NewAttachmentForm = ({
  onAttachmentCreated,
}: {
  onAttachmentCreated: () => void;
}) => {
  const [attachment, setAttachment] = useState<{ title?: string; content?: string }>({
    title: JSON.stringify([
      {
        type: 'paragraph',
        children: [{ text: '', color: theme.colors.black }],
        align: 'left',
        size: 32,
      },
    ]),
    content: JSON.stringify([
      {
        type: 'paragraph',
        children: [{ text: '', color: theme.colors.black }],
        align: 'left',
        size: 32,
      },
    ]),
  });

  const [uploadedFileId, setUploadedFileId] = useState<string>();

  const dataDispatch = useDesignerDataContext(context => context.dispatch);
  const promptInputs = useDesignerDataContext(
    c => c.state.localTemplate?.aiContent?.promptInputs,
  );

  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');

  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);

  const { availablePromptDependencies } = useAvailablePromptDependencies({
    promptId: 'notused',
    promptInputs,
    promptOutputs,
  });

  const { createAttachment, isCreatingAttachment } = useCreateAttachment();
  const {
    // createFromFile,
    createdContent,
    // isCreating,
    reset,
  } = useCreateAttachmentContentFromFile();

  useEffect(() => {
    if (createdContent) {
      setAttachment({
        ...attachment,
        content: JSON.stringify(createdContent.content),
        title: JSON.stringify([
          {
            type: 'paragraph',
            children: [{ text: createdContent.title, color: theme.colors.black }],
            align: 'left',
            size: 32,
          },
        ]),
      });
      setUploadedFileId(createdContent.type);
      reset();
    }
  }, [attachment, createdContent, reset]);

  const handleAddAttachment = () => {
    if (attachment.title && attachment.content) {
      createAttachment({
        data: {
          title: attachment.title,
          content: attachment.content,
        },
        onSuccessFunction: (data: LessonAttachment) => {
          dataDispatch({
            type: TemplateStateActions.attachmentCreated,
            attachment: data,
          });
          onAttachmentCreated();
        },
      });
    }
  };

  // const handleFileUploaded = (uploadedFile: UploadedFile) => {
  //   createFromFile({
  //     data: { fileId: uploadedFile.uploadedFileId, fileType: uploadedFile.fileType },
  //   });
  // };

  return (
    <>
      <div style={{ display: 'flex', marginTop: '0px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>Title</p>
      </div>
      <PromptContainer>
        <PromptTextEditor
          availablePromptDependencies={
            isAITemplatesEnabled ? availablePromptDependencies : []
          }
          value={attachment?.title}
          maxLength={150}
          slideId={`title.${uploadedFileId}`}
          placeholder={'A poem about the ocean'}
          style={{ borderRadius: '5px' }}
          onChange={title => setAttachment({ ...attachment, title })}
        />
      </PromptContainer>
      <div style={{ display: 'flex', marginTop: '20px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>Text to read</p>
      </div>
      <PromptContainer style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <PromptTextEditor
          availablePromptDependencies={
            isAITemplatesEnabled ? availablePromptDependencies : []
          }
          value={attachment?.content}
          maxLength={90000}
          slideId={`attachment.${uploadedFileId}`}
          placeholder={'Paste the passage here'}
          style={{ borderRadius: '5px', minHeight: '150px' }}
          onChange={content => setAttachment({ ...attachment, content })}
        />
      </PromptContainer>

      <div style={{ marginTop: 20 }}>
        <Button
          styles={{ width: '100%' }}
          type="primary"
          onClick={() => handleAddAttachment()}
          loading={isCreatingAttachment}
        >
          Add
        </Button>
      </div>
      {/* <div style={{ width: '100%' }}>
        {isCreating && (
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 20, color: 'white' }} spin />}
          />
        )}
        {!isCreating && (
          <UploadFileInput
            onFileUploaded={handleFileUploaded}
            uploadedFile={undefined}
            setUploadedFIle={() => {
              console.log('');
            }}
          />
        )}
      </div> */}
    </>
  );
};

const PromptContainer = styled.div`
  p {
    font-size: 14px;
    font-weight: bold;
    color: #282f33;
  }
`;

const AttachmentsTab = () => {
  const dataDispatch = useDesignerDataContext(context => context.dispatch);
  const dispatch = useDesignerUIContext(c => c.dispatch);
  const onAddElements = useDesignerDataContext(context => context.handleElementsAdd);
  const tabState = useDesignerUIContext(context => context.state.selectedTab);

  const attachments = useDesignerDataContext(attachmentsSelector);
  const presentationId = useDesignerDataContext(presentationIdSelector);

  const attachmentToEdit = useMemo(() => {
    if (tabState.dataState?.attachmentTab?.attachmentId) {
      return attachments?.find(
        a => a.attachmentId === tabState.dataState?.attachmentTab?.attachmentId,
      );
    }
  }, [attachments, tabState]);

  const visualAttachments = useMemo(
    () =>
      attachments?.map(a => ({
        attachmentId: a.attachmentId,
        title: parseSlateStringToText(a.title),
      })),
    [attachments],
  );

  const handleAttachmentCreated = () => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'attachments',
        subTab: undefined,
      },
    });
  };

  const handleAttachmentEdited = () => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'attachments',
        subTab: undefined,
      },
    });
  };

  const handleAttachmentSelected = (attachmentId: string) => {
    if (!attachments) return;
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'attachments',
        subTab: 'editAttachment',
        dataState: {
          attachmentTab: {
            attachmentId,
          },
        },
      },
    });
  };

  const handleCreateShortcut = (attachmentId: string) => {
    const attachment = attachments?.find(a => a.attachmentId === attachmentId);
    if (!attachment) return;
    const slideElement: SlideElement = {
      type: 'attachmentPassage',
      id: generateUniqueId().toString(),
      x: 1000,
      y: 500,
      height: 420,
      width: 420,
      version: 1,
      angle: 0,
      borderRadius: 0,
      opacity: 1,

      attachmentId: attachment?.attachmentId,
    };

    onAddElements([slideElement]);
  };
  const handleDeleteAttachment = (attachmentId: string) => {
    dataDispatch({
      type: TemplateStateActions.attachmentDeleted,
      attachmentId: attachmentId,
    });
  };

  const handleAttachmentAdjusted = (attachment: LessonAttachment) => {
    dataDispatch({
      type: TemplateStateActions.attachmentCreated,
      attachment,
    });
  };

  const content = (attachmentId: string) => (
    <AttachmentMenu
      handleAttachmentAdjusted={handleAttachmentAdjusted}
      attachmentId={attachmentId}
      presentationId={presentationId}
      handleCreateShortcut={handleCreateShortcut}
      handleDeleteAttachment={handleDeleteAttachment}
    />
  );

  return (
    <TabContainer
      style={{
        padding: '20px 0px 0px 0px',
        display: 'grid',
        gridTemplateRows: '40px auto',
      }}
    >
      {tabState.subTab === 'newAttachment' && (
        <>
          <BackButtonRowContainer
            onClick={() => {
              dispatch({
                type: DesignerUIStateActions.setSelectedTab,
                payload: {
                  tab: 'attachments',
                  subTab: undefined,
                },
              });
            }}
          >
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <Title $fontSize={20}>Add passage</Title>
          </BackButtonRowContainer>
          <ContentContainer style={{ height: 'calc(100% - 40px)', padding: '0px 20px' }}>
            <NewAttachmentForm onAttachmentCreated={handleAttachmentCreated} />
          </ContentContainer>
        </>
      )}
      {tabState.subTab === 'editAttachment' && (
        <>
          <BackButtonRowContainer
            onClick={() => {
              dispatch({
                type: DesignerUIStateActions.setSelectedTab,
                payload: {
                  tab: 'attachments',
                  subTab: undefined,
                },
              });
            }}
          >
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <Title $fontSize={20}>Edit passage</Title>
          </BackButtonRowContainer>
          <ContentContainer style={{ height: 'calc(100% - 40px)', padding: '0px 20px' }}>
            {attachmentToEdit && (
              <EditAttachmentForm
                onDone={handleAttachmentEdited}
                attachment={attachmentToEdit}
              />
            )}
          </ContentContainer>
        </>
      )}
      {tabState.subTab === undefined && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 20px',
              padding: '0px 20px',
            }}
          >
            <Title $fontSize={20}>{'Materials'}</Title>
            <IconWrapper
              onClick={() => {
                dispatch({
                  type: DesignerUIStateActions.setSelectedTab,
                  payload: {
                    tab: 'attachments',
                    subTab: 'newAttachment',
                  },
                });
              }}
            >
              <PlusSvg />
            </IconWrapper>
          </div>
          <ContentContainer style={{ height: 'calc(100% - 40px)', padding: '0px 20px' }}>
            {visualAttachments?.map(attachment => (
              <AttachmentListItem
                key={attachment.attachmentId}
                onClick={() => handleAttachmentSelected(attachment.attachmentId)}
              >
                <span>{attachment.title}</span>
                <HoveredMenuToggleContainer onClick={e => e.stopPropagation()}>
                  <StyledPopover
                    trigger={'click'}
                    content={() => content(attachment.attachmentId)}
                    placement="bottomRight"
                    arrowContent={<></>}
                    arrowPointAtCenter={true}
                    style={{ padding: 0 }}
                  >
                    <IconWrapper height="20px" width="20px">
                      <MoreSvg />
                    </IconWrapper>
                  </StyledPopover>
                </HoveredMenuToggleContainer>
                <IconWrapper height="30px" width="30px">
                  <ArrowRightSvg />
                </IconWrapper>
              </AttachmentListItem>
            ))}
          </ContentContainer>
        </>
      )}
    </TabContainer>
  );
};

const AttachmentMenu = ({
  handleCreateShortcut,
  handleDeleteAttachment,
  attachmentId,
  presentationId,
  handleAttachmentAdjusted,
}: {
  handleCreateShortcut: (attachmentId: string) => void;
  handleDeleteAttachment: (attachmentId: string) => void;
  attachmentId: string;
  presentationId?: string;
  handleAttachmentAdjusted: (attachment: LessonAttachment) => void;
}) => {
  const {
    adjustReadability,
    adjustedAttachment,
    // isLoading,
    reset,
  } = useAdjustAttachmentReadability();

  useEffect(() => {
    if (adjustedAttachment) {
      handleAttachmentAdjusted(adjustedAttachment);
      reset();
    }
  }, [adjustedAttachment, handleAttachmentAdjusted, handleCreateShortcut, reset]);

  const [isAdjustingReadability, setIsAdjustingReadability] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<string>('5th');

  return (
    <HoveringSlideMenuContainer>
      <HoveringSlideMenuItem
        role="button"
        onClick={() => handleCreateShortcut(attachmentId)}
      >
        <IconWrapper color={theme.colors.black} height="16px" width="16px">
          <LinkSvg />
        </IconWrapper>
        <span
          style={{
            fontSize: 16,
            flex: 1,
            minWidth: 70,
            color: theme.colors.black,
            fontWeight: 'bold',
          }}
        >
          Add shortcut to slide
        </span>
      </HoveringSlideMenuItem>
      {/* <HoveringSlideMenuItem
        role="button"
        onClick={() => setIsAdjustingReadability(true)}
      >
        <IconWrapper color={theme.colors.black} height="16px" width="16px">
          {isLoading ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
          ) : (
            <MagicWandSvg />
          )}
        </IconWrapper>
        <span
          style={{
            fontSize: 16,
            flex: 1,
            minWidth: 70,
            color: theme.colors.black,
            fontWeight: 'bold',
          }}
        >
          Adjust readability
        </span>
      </HoveringSlideMenuItem> */}
      {isAdjustingReadability && (
        <HoveringSlideMenuItemExpan>
          <Select
            style={{ width: '100%' }}
            label="Grade"
            onSelect={value => setSelectedGrade(value as string)}
            options={[
              { displayName: '1st', value: '1st' },
              { displayName: '2nd', value: '2nd' },
              { displayName: '3rd', value: '3rd' },
              { displayName: '4th', value: '4th' },
              { displayName: '5th', value: '5th' },
              { displayName: '6th', value: '6th' },
              { displayName: '7th', value: '7th' },
              { displayName: '8th', value: '8th' },
              { displayName: '9th', value: '9th' },
              { displayName: '10th', value: '10th' },
              { displayName: '11th', value: '11th' },
              { displayName: '12th', value: '12th' },
            ]}
          ></Select>
          <Button
            styles={{ width: '100%', height: '42px', marginTop: 5 }}
            type="primary"
            disabled={!selectedGrade}
            onClick={() => {
              adjustReadability({
                attachmentId,
                grade: selectedGrade,
                presentationId: presentationId || '',
              });
              setIsAdjustingReadability(false);
            }}
          >
            Done
          </Button>
        </HoveringSlideMenuItemExpan>
      )}
      <HoveringSlideMenuItem
        role="button"
        onClick={() => handleDeleteAttachment(attachmentId)}
      >
        <IconWrapper color={'#D84C42'} height="16px" width="16px">
          <DeleteSvg />
        </IconWrapper>
        <span
          style={{
            fontSize: 16,
            flex: 1,
            minWidth: 70,
            color: '#D84C42',
            fontWeight: 'bold',
          }}
        >
          Delete
        </span>
      </HoveringSlideMenuItem>
    </HoveringSlideMenuContainer>
  );
};

export const UploadFileInput = ({
  onFileUploaded,
  uploadedFile,
  tenantId,
}: // setUploadedFIle,
{
  onFileUploaded: (uploadedFile: UploadedFile) => void;
  uploadedFile?: UploadedFile; // .pdf .jpeg or .png
  setUploadedFIle: (d: UploadedFile) => void;
  tenantId: string;
}) => {
  const [isFileFormatErrorVisible, setIsFileFormatErrorVisible] = useState(false);

  const {
    fileInfo: presentationFileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingPresentation,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingPresentation,
    reset: resetPresentationUpload,
  } = useUploadPresentation();

  const {
    fileInfo: googleDriveFileInfo,
    upload: uploadGoogleDrive,
    isUploading: isUploadingGoogleDrive,
    isSuccess: uploadGoogleDriveSuccess,
    isError: isErrorUploadingGoogleDrive,
    reset: resetGoogleDriveUpload,
  } = useUploadGoogleDrive(tenantId);

  const metrics = useMetrics();
  const { isReady: googleDriveIsReady, isError: googleDriveIsError } = useGoogleDrive(
    tenantId,
  );
  const isGoogleDriveFlagEnabled = useFlag('google_drive_integration');

  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);
  const [presentation, setPresentation] = useState<File | undefined>(undefined);
  const [googleDriveFile, setGoogleDriveFile] = useState<CallbackDoc | undefined>(
    undefined,
  );
  const isDesktop = useBreakPoint(700);
  const handleLocalFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      metrics.logEvent('Creator.Upload.Presentation.Generator.Selected', {
        fileSize: fileSizeInMb,
        fileType: file.type,
      });
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        setPresentation(file);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [metrics, uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && presentationFileInfo && presentation) {
      onFileUploaded({
        name: presentation.name,
        fileType: presentationFileInfo.fileType,
        uploadedFileId: presentationFileInfo.filePath,
      });
      resetPresentationUpload();
    }
  }, [
    presentationFileInfo,
    uploadPresentationSuccess,
    resetPresentationUpload,
    onFileUploaded,
    presentation,
  ]);

  const handleGoogleDriveFileSelected = useCallback(
    (file: CallbackDoc) => {
      const fileSizeInMb = file.sizeBytes * 1e-6;
      metrics.logEvent('Creator.Upload.GoogleDrive.Generator.Selected', {
        fileSize: fileSizeInMb,
        fileType: file.type,
      });
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        setGoogleDriveFile(file);
        uploadGoogleDrive({ fileId: file.id });
      } else {
        setFileSizeExceeded(true);
      }
    },
    [metrics, uploadGoogleDrive],
  );
  const handleClearUploadedFile = () => {
    // Reset local file
    resetPresentationUpload();
    setPresentation(undefined);
    // Reset google drive file
    resetGoogleDriveUpload();
    setGoogleDriveFile(undefined);
  };

  const { showFilePicker: showGoogleDrivePicker } = useGoogleDrivePicker(
    handleGoogleDriveFileSelected,
    tenantId,
    'DOCS',
    'application/pdf,pdf',
  );

  // Add ref for file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modified showLocalFilePicker function
  const showLocalFilePicker = useCallback(() => {
    if (!fileInputRef.current) {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.id = 'fileInput';
      fileInput.name = 'myFile';
      fileInput.multiple = false;
      fileInput.accept = isIos
        ? '*'
        : '.pptx, .key, .ppt, .pdf, .png, .jpg, .jpeg, .webp';
      fileInput.style.display = 'none';

      fileInput.onchange = () => {
        const supportedFormats = [
          '.pptx',
          '.key',
          '.ppt',
          '.pdf',
          '.png',
          '.jpg',
          '.jpeg',
          '.webp',
        ];

        if (fileInput.files && fileInput.files.length > 0) {
          const fileName = fileInput.files[0].name;
          const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
          const isSupportedFormat = supportedFormats.includes(
            fileExtension.toLowerCase(),
          );
          if (!isSupportedFormat) {
            setIsFileFormatErrorVisible(true);
            return;
          }
          handleLocalFileSelected(fileInput.files[0]);
        }
      };

      fileInput.onclick = () => {
        fileInput.value = '';
      };

      fileInputRef.current = fileInput;
      document.body.appendChild(fileInput);
    }

    fileInputRef.current.click();
  }, [handleLocalFileSelected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fileInputRef.current) {
        document.body.removeChild(fileInputRef.current);
        fileInputRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (uploadGoogleDriveSuccess && googleDriveFileInfo && googleDriveFile) {
      onFileUploaded({
        name: googleDriveFile.name,
        fileType: googleDriveFileInfo.fileType,
        uploadedFileId: googleDriveFileInfo.filePath,
      });
      resetGoogleDriveUpload();
    }
  }, [
    googleDriveFileInfo,
    uploadGoogleDriveSuccess,
    resetGoogleDriveUpload,
    onFileUploaded,
    googleDriveFile,
  ]);

  // Joint status variables for both upload methods
  const uploadStatus = useMemo(() => {
    const isOngoing = isUploadingPresentation || isUploadingGoogleDrive;
    const isError = isErrorUploadingPresentation || isErrorUploadingGoogleDrive;
    const isSuccess = uploadPresentationSuccess || uploadGoogleDriveSuccess;
    const file = uploadedFile;
    const isStarted = !!file || isOngoing || isError || isSuccess;
    return {
      isOngoing,
      isError,
      isSuccess,
      isStarted,
      file,
    };
  }, [
    isUploadingPresentation,
    isUploadingGoogleDrive,
    isErrorUploadingPresentation,
    isErrorUploadingGoogleDrive,
    uploadPresentationSuccess,
    uploadGoogleDriveSuccess,
    uploadedFile,
  ]);

  const handleDropdownSelect = (option: string) => {
    if (option === 'local') {
      metrics.logEvent('Creator.Upload.Presentation.Generator.Clicked');
      showLocalFilePicker();
    }
    if (option === 'googleDrive') {
      metrics.logEvent('Creator.Upload.GoogleDrive.Generator.Clicked');
      showGoogleDrivePicker();
    }
  };

  return (
    <>
      <BaseModal
        visible={isFileFormatErrorVisible}
        onCancel={() => setIsFileFormatErrorVisible(false)}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage>
            <>
              <ErrorMessage>{'Unsupported file format'}</ErrorMessage>

              <ErrorMessage style={{ fontSize: '18px' }}>
                {'Please upload a file with .pdf, .jpeg, or .png format.'}
              </ErrorMessage>
            </>
          </ErrorMessage>
          <Button
            styles={{ marginTop: 20 }}
            width="120px"
            onClick={() => setIsFileFormatErrorVisible(false)}
          >
            Ok
          </Button>
        </div>
      </BaseModal>

      {!uploadStatus.isStarted && (
        <FloatingSelect
          width={'100%'}
          value={undefined}
          placeholder="Select Presentation"
          options={[
            {
              value: 'local',
              displayName: 'Upload (.pdf, .jpeg, .png)',
              icon: <UploadsSvg />,
            },
            {
              value: 'googleDrive',
              displayName: isGoogleDriveFlagEnabled
                ? 'Google Drive'
                : 'Google Drive (Soon)',
              icon: !isGoogleDriveFlagEnabled ? (
                <GoogleDriveSvg />
              ) : googleDriveIsError ? (
                <ExclamationMarkSvg />
              ) : !googleDriveIsReady ? (
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{
                        fontSize: 20,
                        color: theme.colors.orange,
                        marginRight: 10,
                      }}
                      spin
                    />
                  }
                />
              ) : (
                <GoogleDriveSvg />
              ),
              disabled: !isGoogleDriveFlagEnabled || !googleDriveIsReady,
            },
          ]}
          onSelect={s => handleDropdownSelect(s)}
        />
      )}
      {uploadStatus.isOngoing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: `${theme.colors.white}`,
            borderRadius: 5,
            padding: '9px 15px',
            position: 'relative',
            boxShadow: theme.effects.floatingCreatorElementShadow,
            margin: isDesktop ? '0' : '10px 0',
          }}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 20,
                  color: theme.colors.orange,
                  marginRight: 10,
                }}
                spin
              />
            }
          />{' '}
          Uploading...
        </div>
      )}
      {uploadStatus.file && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: isDesktop ? 200 : '100%',
            background: `${theme.colors.paleBeige}`,
            borderRadius: 5,
            padding: '9px 15px',
            position: 'relative',
            boxShadow: theme.effects.floatingCreatorElementShadow,
            margin: isDesktop ? '0' : '10px 0',
          }}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <IconWrapper height="12px" width="12px" style={{ marginRight: 5 }}>
            <FolderSvg />
          </IconWrapper>
          <p
            style={{
              whiteSpace: 'nowrap',
              maxWidth: isDesktop ? 200 : '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: 0,
              marginLeft: 5,
            }}
          >
            {uploadStatus.file.name}
          </p>{' '}
          <IconWrapper
            height="8px"
            width="8px"
            style={{
              marginBottom: 10,
              boxShadow: theme.effects.magicFloatingShadow,
              borderRadius: 50,
              padding: 5,
              background: theme.colors.greyLight,
              position: 'absolute',
              right: '-8px',
              top: '-8px',
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleClearUploadedFile();
            }}
          >
            <CrossSvg />
          </IconWrapper>
        </div>
      )}
      {fileSizeExceeded && (
        <div style={{ display: 'inline-block' }}>The file was too large</div>
      )}
      {uploadStatus.isError && <div>Failed to upload file</div>}
    </>
  );
};

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const HoveringSlideMenuItem = styled.div`
  width: calc(100% + 32px);
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  margin-left: -16px;
  margin-right: -16px;
  padding: 10px 25px;

  transition: background-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: rgba(40, 47, 51, 0.1);
  }
  &:active {
    background: rgba(40, 47, 51, 0.2);
  }

  cursor: pointer;
`;

const HoveringSlideMenuItemExpan = styled(HoveringSlideMenuItem)`
  height: 150px;
  display: block;
`;

const HoveringSlideMenuContainer = styled.div<{ $rightAbsolutePosition?: boolean }>`
  border-radius: 10px;
  background-color: white;
  height: 100%;
  width: 100%;
`;

const StyledPopover = styled(Popover)`
  .ant-popover-inner {
    border-radius: 10px;
    box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.1);
    background-color: red;
    padding: 0;
    border: none;
  }
`;

const HoveredMenuToggleContainer = styled.div`
  padding: 0px 3px 0px 3px;
  border-radius: 3px;
  transition: background-color 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: rgba(40, 47, 51, 0.6);
  }
  &:active {
    background: rgba(40, 47, 51, 0.8);
  }
`;

const AttachmentListItem = styled.div`
  padding: 10px 20px;
  width: 100%;
  border-radius: 10px;
  box-shadow: 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13),
    0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11);
  display: grid;
  grid-template-columns: 205px 30px 30px;
  align-items: center;
  cursor: pointer;

  span {
    font-size: 14px;
    line-height: 14px;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const ContentContainer = styled.div`
  height: calc(100% - 50px);
  overflow-y: auto;

  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const BackButtonRowContainer = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 0px 10px;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;
  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

export default AttachmentsTab;
